import type { CapabilityCode, ProjectDuty, SystemRole } from '@youkuai/contracts';

export type ProjectWorkspaceMode = 'FULL' | 'EXECUTOR' | 'NONE';

const fullWorkspaceDuties = new Set<ProjectDuty>([
  'PROJECT_MANAGER', 'PACKAGE_MANAGER', 'START_CONFIRMER', 'DELIVERY_CONFIRMER', 'CONTENT_PLANNER',
]);
const executionDuties = new Set<ProjectDuty>([
  'TASK_EXECUTOR', 'VIDEO_EDITOR', 'VIDEO_PRODUCTION',
]);

export interface ProjectWorkspaceAccess {
  mode: ProjectWorkspaceMode;
  administrator: boolean;
  projectManager: boolean;
  deliveryConfirmer: boolean;
  planner: boolean;
  executor: boolean;
  publisher: boolean;
  canManageProject: boolean;
  canCreateContent: boolean;
  canAssignTasks: boolean;
  canProgressTasks: boolean;
  canReturnSubmittedTasks: boolean;
  canResolveIssues: boolean;
}

/**
 * Account capabilities answer "what can this account ever do"; project duties
 * answer "what does this person do here".  Effective UI access is their
 * intersection, so a mixed-role account can be a planner in one project and
 * an executor in another without leaking the planner workspace across both.
 */
/**
 * BUG-0130/0131：账号同时具备「内容策划」与「任务执行人」时按能力叠加处理。
 * 只有纯执行人（无策划能力、无管理角色）才进入执行人视图。
 */
export function isPureExecutorSession(input: {
  roles: readonly SystemRole[]; capabilities: readonly CapabilityCode[];
}): boolean {
  return input.roles.includes('EXECUTOR') &&
    !input.roles.some((role) => role === 'ADMIN' || role === 'ADMINISTRATION' || role === 'PROJECT_MANAGER') &&
    !input.capabilities.includes('content.create');
}

export function resolveProjectWorkspaceAccess(input: {
  roles: readonly SystemRole[];
  capabilities: readonly CapabilityCode[];
  membershipId: string | null;
  duties: readonly ProjectDuty[];
}): ProjectWorkspaceAccess {
  const roles = new Set(input.roles);
  const capabilities = new Set(input.capabilities);
  const duties = new Set(input.duties);
  const administrator = roles.has('ADMIN');
  const projectManager = duties.has('PROJECT_MANAGER') || duties.has('PACKAGE_MANAGER');
  const planner = duties.has('CONTENT_PLANNER');
  const deliveryConfirmer = duties.has('DELIVERY_CONFIRMER');
  const executor = input.membershipId !== null && input.duties.some((duty) => executionDuties.has(duty));
  const publisher = input.membershipId !== null && duties.has('PUBLICATION_OPERATOR');
  const full = administrator || input.duties.some((duty) => fullWorkspaceDuties.has(duty));
  const scopedWriter = administrator || projectManager || planner;
  return {
    mode: full ? 'FULL' : executor ? 'EXECUTOR' : 'NONE',
    administrator,
    projectManager,
    deliveryConfirmer,
    planner,
    executor,
    publisher,
    canManageProject: administrator || duties.has('PROJECT_MANAGER'),
    canCreateContent: capabilities.has('content.create') && scopedWriter,
    canAssignTasks: capabilities.has('task.assign') && scopedWriter,
    canProgressTasks: capabilities.has('task.progress') && (administrator || input.membershipId !== null),
    canReturnSubmittedTasks: capabilities.has('project.read') &&
      (duties.has('PROJECT_MANAGER') || deliveryConfirmer),
    canResolveIssues: capabilities.has('content.issue_resolve') && scopedWriter,
  };
}
